import type { Timestamp } from 'firebase/firestore'
import type { Entry } from './team'

/**
 * An organisation saying "we are at this event".
 *
 * It is the only thing that causes capture. Nothing is captured for an event
 * with no attachments, however addressable it is — see §12 of
 * docs/timing-pipeline.md. Without that gate the rule would be "capture
 * everything we can", which for TSL means all 111 catalogued events, none of
 * which anybody asked for.
 *
 * Stored at events/{eventId}/attachments/{orgId}, so an organisation can attach
 * once and only once, and the reconciler can ask "does anybody want this" with
 * a single subcollection read.
 *
 * Which events an organisation is attending is private to that organisation:
 * attaching reveals interest, and the brief already treats which cars a team
 * cares about as private.
 */
export interface AttachmentDoc {
  /** Denormalised so the reconciler and the admin view can name it without a
   *  second read. Organisations are renamed rarely and this is not a source of
   *  truth for the name. */
  orgName: string
  attachedAt: Timestamp
  attachedBy: string
  /**
   * Overrides the default capture window for this event.
   *
   * The default is 08:00–18:00 local on every day the event runs, which covers
   * a normal meeting. A 25-hour race does not stop at six in the evening, and
   * the organisation attaching is the one who knows that — so they say so here
   * rather than waiting for a timetable that may not be published for months.
   */
  captureFrom?: string | null
  captureUntil?: string | null
  /** Set when the event runs continuously through the night, which is the same
   *  statement as "capture the whole span" and is easier to mean. */
  continuous?: boolean
  /** Free text for whoever picks this up later. */
  note?: string | null
  /**
   * What the organisation is running at this event.
   *
   * Here rather than in a collection of its own because an entry at an event
   * you are not attending is meaningless, and this document is already the
   * organisation-to-event join. A team runs a handful of cars, so the array
   * stays small and can be read and written atomically with the attachment.
   */
  entries?: Entry[]
}

/** The default capture window, in the event's own local time. No real meeting
 *  runs on track before this. */
export const DEFAULT_CAPTURE_FROM = '08:00'
/** …or after this. */
export const DEFAULT_CAPTURE_UNTIL = '18:00'

/**
 * Describes what will actually be captured, for the person deciding whether to
 * attach.
 *
 * Written as a sentence rather than a set of fields because the question being
 * answered is "will my race be recorded", and a start and end time in a table
 * does not answer it.
 */
export function describeCaptureWindow(a: Pick<AttachmentDoc, 'captureFrom' | 'captureUntil' | 'continuous'>): string {
  if (a.continuous) return 'Continuously, through the night, for the whole event'
  const from = a.captureFrom ?? DEFAULT_CAPTURE_FROM
  const until = a.captureUntil ?? DEFAULT_CAPTURE_UNTIL
  return `${from}–${until} local, on every day of the event`
}
